/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  AlertTriangle, Clock, Loader2, Send, UserCheck, UserMinus, X,
} from 'lucide-react';
import { shiftReplacementAPI } from '../../lib/api';

const URGENCY_BADGE = {
  today: { cls: 'bg-rose-100 text-rose-800 border-rose-200', label: 'TODAY' },
  tomorrow: { cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'TOMORROW' },
  future: { cls: 'bg-slate-100 text-slate-600 border-slate-200', label: 'FUTURE' },
};

const WARNING_LABELS = {
  overtime_risk: 'OT risk',
  near_max_hours: 'Near max hours',
  role_mismatch: 'Role mismatch',
  has_pending_leave: 'Pending leave',
};

/**
 * Manager-facing "Find Replacement" dialog.
 *
 * Props:
 *   open, onOpenChange — standard dialog control
 *   shiftId            — id of the existing schedule row
 *   onSent             — optional callback after a request is created
 */
export function ReplacementDialog({ open, onOpenChange, shiftId, onSent }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [sendingFor, setSendingFor] = useState(null);

  const load = useCallback(async () => {
    if (!shiftId) return;
    setLoading(true);
    try {
      const r = await shiftReplacementAPI.suggest(shiftId);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    if (open) {
      setNote('');
      load();
    }
  }, [open, load]);

  const sendRequest = async (candidateUserId) => {
    setSendingFor(candidateUserId);
    try {
      await shiftReplacementAPI.create({
        shift_id: shiftId,
        candidate_user_id: candidateUserId,
        note: note || null,
      });
      toast.success('Replacement request sent');
      onSent && onSent();
      load(); // refresh so the same candidate now shows "already requested"
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.info('A pending request already exists for this candidate');
        load();
      } else {
        toast.error(e?.response?.data?.detail || 'Send failed');
      }
    } finally {
      setSendingFor(null);
    }
  };

  const urgency = URGENCY_BADGE[data?.urgency || 'future'];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="replacement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Find replacement
            {data && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${urgency.cls}`} data-testid="replacement-urgency">
                {urgency.label}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {data?.shift ? (
              <span className="font-mono text-xs">
                {data.shift.date} · {data.shift.start_time}–{data.shift.end_time}{data.shift.role ? ` · ${data.shift.role}` : ''}
              </span>
            ) : 'Loading shift…'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Optional note for the candidate</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Original employee called in sick"
              data-testid="replacement-note"
            />
          </div>

          {loading ? (
            <div className="py-10 text-center text-slate-400">
              <Loader2 className="inline h-4 w-4 mr-2 animate-spin" /> Finding candidates…
            </div>
          ) : !data?.candidates?.length ? (
            <div className="py-10 text-center text-slate-400" data-testid="replacement-empty">
              No eligible candidates. Try checking staff assignments for this location.
            </div>
          ) : (
            <ol className="rounded-lg border divide-y max-h-[420px] overflow-auto" data-testid="replacement-candidates">
              {data.candidates.map((c) => (
                <li key={c.user_id} className="flex items-center gap-3 p-2.5" data-testid={`candidate-${c.user_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span className="truncate">{c.name || c.email}</span>
                      {c.role_match && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">SAME ROLE</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span><Clock className="inline h-3 w-3 mr-0.5" />{c.weekly_hours_scheduled}h this wk → {c.weekly_hours_after_swap}h after</span>
                      {(c.warnings || []).map((w) => (
                        <span
                          key={w}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[10px]"
                          data-testid={`warning-${w}-${c.user_id}`}
                        >
                          <AlertTriangle className="h-2.5 w-2.5" /> {WARNING_LABELS[w] || w}
                        </span>
                      ))}
                    </div>
                  </div>
                  {c.already_requested ? (
                    <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid={`already-requested-${c.user_id}`}>
                      Requested
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => sendRequest(c.user_id)}
                      disabled={sendingFor === c.user_id}
                      data-testid={`send-replacement-${c.user_id}`}
                    >
                      {sendingFor === c.user_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                      ) : (
                        <Send className="h-3.5 w-3.5 mr-1" />
                      )}
                      Send
                    </Button>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="replacement-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReplacementDialog;
